import { Injectable, OnDestroy } from '@angular/core';

import { forkJoin, of, Observable, BehaviorSubject } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, switchMap, take, tap, filter } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { find } from 'lodash';

import { SavedMentionSearchesApi } from '../../../api/mentions/saved-mention-searches.api';
import { SavedMentionSearchApiModel } from '../../../api/mentions/model/saved-mention-search.api.model';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { ExternalDataApi } from '../../../api/externalData/external-data.api';
import { SearchCriteriaModel } from '../../../api/mentions/model/search-criteria.model';
import { DataConnectorTypesEnum } from '../../../api/externalData/enums/data-connector-types.enum';
import { MediaSubscriptionService } from '../../../Monitor/Core/services/media-subscription.service';
import { ThemeService } from '../../../Social/Intelligence/services/theme.service';
import { DataConnectorsApi } from '../../../api/externalData/data-connectors.api';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { IndexeddbCoreCacheService } from '../../../Core/Cache/services/indexeddb-core-cache.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { MentionSearchModel } from '../../../Monitor/Shared/models/mention-search.model';

import { ReportsSeriesService } from '../../Reports/services/reports-series.service';
import { ReportsExternalDataMapperService } from '../../Reports/services/report-external-data-mapper.service';
import { ReportSeriesModel } from '../../Reports/models/report-series.model';
import { ReportsSeriesMapperService } from '../../Reports/services/reports-series-mapper.service';

import { DashboardService } from './dashboard.service';
import { BaseSeriesService } from './base-series.service';
import { DashboardModel } from '../models/dashboard.model';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { DashboardPageBuilderService } from '../services/dashboard-page-builder.service';
import { ReportBlockTypeEnum } from '../../Reports/enums/report-block-type.enum';
import { DataConnectionService } from '../../../Core/Users/services/data-connection.service';
import { ReportsSeriesDataMapperService } from '../../../Common/Reports/services/reports-series-data-mapper.service';
import { AgentService } from '../../../Social/Intelligence/services/agent.service';

@Injectable()
export class DashboardSeriesService extends ReportsSeriesService implements OnDestroy {
    private currentDashboard: DashboardModel;

    constructor(
        public savedSearchesApi: SavedMentionSearchesApi,
        public mentionsApi: MentionsApi,
        public reportsSeriesMapperService: ReportsSeriesMapperService,
        public localizationService: LocalizationService,
        public externalDataMapperService: ReportsExternalDataMapperService,
        public messageService: MessageService,
        public currentUserStateService: CurrentUserStateService,
        public externalDataApi: ExternalDataApi,
        public indexedDbCoreCacheService: IndexeddbCoreCacheService,
        private baseSeriesService: BaseSeriesService,
        private dashboardService: DashboardService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public themeService: ThemeService,
        public currencyConversionService: CurrencyConversionService,
        public dashboardPageBuildService: DashboardPageBuilderService,
        public dataConnectionService: DataConnectionService,
        public reportsSeriesDataMapperService: ReportsSeriesDataMapperService,
        public dataConnectorsApiService: DataConnectorsApi,
        public agentService: AgentService
    ) {
        super(
            savedSearchesApi,
            mentionsApi,
            reportsSeriesMapperService,
            localizationService,
            null,
            externalDataMapperService,
            messageService,
            currentUserStateService,
            externalDataApi,
            indexedDbCoreCacheService,
            mediaSubscriptionService,
            themeService,
            currencyConversionService,
            dataConnectionService,
            reportsSeriesDataMapperService,
            dataConnectorsApiService,
            agentService
        );
        this.setSubscriptions();
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    /**
     * Sets subscriptions, pushing to array for easy cleanup later
     */
    setSubscriptions(): void {
        this.subscriptions = [];
        this.subscriptions.push(
            this.dashboardService?.getCurrentDashboard().subscribe(
                currentDashboard => {
                    if (!currentDashboard) {
                        this.clearMapping();
                        this.isRefresh = false;

                        return;
                    }
                    if (this.currentDashboard !== currentDashboard && this.currentDashboard?.id !== currentDashboard.id) {
                        // report changed, clear out old data.
                        this.clearMapping();
                        this.isRefresh = false;
                    }
                    this.currentDashboard = currentDashboard;
                    this.prepareReport();
                }
            ),
            this.dataConnectionService?.googleAnalyticsDataConnectors$.pipe(filter(connectors => !!connectors)).subscribe(
                connectors => {
                    this.googleAnalyticsDataConnectors = connectors;

                    if (this.currentDashboard?.getExternalDataSeries()) {
                        this.prepareExternalSeries();
                    }
                }
            ),
            this.agentService.all$.pipe(filter(x => !!x)).subscribe(
                agents => this.agents = agents
            ),
            this.currentUserStateService.getPreferences().pipe(filter(preferences => !!preferences)).subscribe(
                preferences => {
                    this.userTimezonePreference = preferences.timeZone;
                }
            ),
            this.themeService.all$.subscribe((socialThemesList) => {
                this.socialThemes = socialThemesList;
            })
        );
    }

    public prepareReport(): void {
        this.setupDataBeforePrepare();
        this.prepareExternalSeries();
    }

    /**
     * function handles to call prepareMentionSeries() after the mention data is returned
     * TODO: will need logic when doing account availability
     */
    setupDataBeforePrepare(): void {
        this.currentUserStateService.getPermissions().pipe(
            filter(x => !!x),
            switchMap(permissions => {
                if (permissions.includes(userPermissionsEnum.advancedMonitoring)) {
                    // on page load, since the logic is always getting the top X topics,
                    // we must wait for topics to be loaded before we can continue

                    return this.baseSeriesService.topicsService.getTopics().pipe(
                        filter(x => !!x),
                        switchMap(() => {
                            // will handle setting the targeting & monitoring data to the series
                            return this.prepareMentionSeries();
                        })
                    );
                } else {
                    return this.prepareMentionSeries();
                }
            }),
            take(1)
        ).subscribe();
    }

    /**
     * Loops over all the series in the report and sets the searchCriteria and topic as required
     */
    prepareMentionSeries(): Observable<MentionsSearchResponseModel[]> {
        this.mentionDataProvidersMap.clear();
        this.mentionDataMap.clear();
        const searchesMap: Map<SearchCriteriaModel, ReportSeriesModel[]> = new Map();

        // some series (topic and subscriptions right now) save their search object in the b/e
        // we need to retrieve this search object in order to perform a facet search using it.
        const seriesSavedSearchRequestMap: Map<ReportSeriesModel, Observable<SavedMentionSearchApiModel>> = new Map();

        for (const series of this.currentDashboard.getMentionsSeries()) {
            if (series.mentions && !series.styles?.ignoreApiCall) {
                let request: Observable<SavedMentionSearchApiModel>;

                this.baseSeriesService.setSeries(series);

                if (series.mentions.topicId) {
                    if (!series.mentions.topic) {
                        request = this.savedSearchesApi.getById(series.mentions.topicId);
                    }
                } else if (series.mentions.mediaSubscriptionId) {
                    if (!series.mentions.mediaSubscription) {
                        const mentionSearchModel = new MentionSearchModel();

                        mentionSearchModel.searchCriteria.query.subscription.include = [series.mentions.mediaSubscriptionId];
                        series.mentions.mediaSubscription = mentionSearchModel;
                    }
                } else {
                    if (!series.hasValidMentionsSource()) {
                        // no topics defined in the series
                        const errorString = this.localizationService.transform('Dashboard.chart.noTopicsToDisplay');
                        // // throw an error for chart model watchData to catch
                        series.dataProvider.error(new Error(`${errorString}`));
                    }
                }
                if (!!request) {
                    seriesSavedSearchRequestMap.set(series, request);

                } else {
                    // This series has no saved search to fetch from the api
                    this.addToSearchMap(searchesMap, series.toSearchCriteriaModel(), series);
                }
            } else if (series.styles?.ignoreApiCall) {
                const seriesKey = series.getSeriesKey();
                if (!this.mentionDataProvidersMap.get(seriesKey)) {
                    this.mentionDataMap.set(seriesKey, null);
                }
            }
        }

        for (const series of this.currentDashboard.getSocialSeries()) {
            // we don't want to make api calls for shared report ones
            if (series.mentions && !series.isSharedReport && !series.styles?.ignoreApiCall) {
                if (series.mentions.socialTopicId && !series.mentions.socialTopic) {
                    const mentionSearchModel = new MentionSearchModel();

                    mentionSearchModel.searchCriteria.query.agent.include = [series.mentions.socialTopicId];
                    series.mentions.socialTopic = mentionSearchModel;
                    series.styles.socialTopicThemeIds = find(this.agents, {id: series.mentions.socialTopicId})?.themeIds;

                } else if (series.mentions.themeId && !series.mentions.theme) {
                    const mentionSearchModel = new MentionSearchModel();

                    mentionSearchModel.searchCriteria.query.theme.include = [series.mentions.themeId];
                    series.mentions.theme = mentionSearchModel;
                    series.styles.socialTopicIds = find(this.socialThemes, {id: series.mentions.themeId})?.subscriptionIds;
                }

                // Social series have no saved search to fetch so put it into search map directly
                this.addToSearchMap(searchesMap, series.toSearchCriteriaModel(), series);
            } else if (series.styles?.ignoreApiCall) {
                const seriesKey = series.getSeriesKey();
                if (!this.mentionDataProvidersMap.get(seriesKey)) {
                    this.mentionDataMap.set(seriesKey, null);
                }
            }
        }

        const savedMentionSearches: Observable<SavedMentionSearchApiModel>[] = [];
        for (const [series, search] of seriesSavedSearchRequestMap.entries()) {
            savedMentionSearches.push(
                search.pipe(
                    tap(response => this.setSeriesSearches(series, [response])),
                    tap(() => this.addToSearchMap(searchesMap, series.toSearchCriteriaModel(), series)),
                    catchError(error => {
                        if (error.status === 404) {
                            // Topic or subscription no longer exists
                            const seriesKey = series.getSeriesKey();
                            if (!this.mentionDataProvidersMap.get(seriesKey)) {
                                // The facetDataMap is empty for this series key, fetch the data for it
                                this.mentionDataMap.set(seriesKey, null);
                                this.mentionDataProvidersMap.set(seriesKey, new BehaviorSubject(null));
                            }

                            const errorString = this.localizationService.transform('Reports.error.topicRemoved');
                            this.mentionDataProvidersMap.get(seriesKey).error(new Error(errorString));
                            series.dataProvider.error(new Error(errorString));
                        }
                        return of(null);
                    })
                )
            );
        }

        // This is complicated and probably shouldn't be here.  This function (prepareMentionSeries) modifies the series, specifically,
        // this.baseService.setSeries, if the dashboardPageBuilder finds out about the blocks before this change, it will not render the RECENT_TOPICS chart
        // properly because the labels won't yet be set.  Ultimately, this "seriesPrepared" sort of event should likely be something that page builder can
        // listen to instead of this service managing when the blocks get set, but that's a larger refactor most likely.
        this.dashboardPageBuildService.setCurrentDashboard(this.currentDashboard);
        return forkJoin(savedMentionSearches).
            pipe(
                defaultIfEmpty(null),
                concatMap(async (_) => {
                    const facetSearches: Observable<MentionsSearchResponseModel>[] = [];

                    for (const [searchCriteria, seriesList] of searchesMap.entries()) {
                        const isFacetSearch = seriesList[0].chartType !== ReportBlockTypeEnum.TOP_MENTIONS;
                        const request = await this.prepareSearch(seriesList, searchCriteria, isFacetSearch);
                        if (request) {
                            facetSearches.push(request);
                        }
                    }
                    return forkJoin(facetSearches);
                }),
                concatMap(v => v)
            );
    }

    public async prepareExternalSeries() {
        if (!this.googleAnalyticsDataConnectors) {
            // Wait for the connectors to come in.
            // The subscription will re-trigger this call when they're available
            return;
        }
        // check that the external sources are still valid
        for (const series of this.currentDashboard.getExternalDataSeries()) {
            if (series.externalData.externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS
                || series.externalData.externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS_4) {
                if (!this.validateGoogleAnalyticsSeries(series)) {
                    const seriesKey = series.getSeriesKey();
                    if (!this.externalDataProvidersMap.get(seriesKey)) {
                        // The facetDataMap is empty for this series key, fetch the data for it
                        this.mentionDataMap.set(seriesKey, null);
                        this.externalDataProvidersMap.set(seriesKey, new BehaviorSubject(null));
                    }

                    const errorString = this.localizationService.transform('Reports.error.externalDataSourceRemoved');
                    this.externalDataProvidersMap.get(seriesKey).error(new Error(errorString));
                    series.mentions.topicId = null;
                    series.dataProvider.error(new Error(errorString));
                    continue;
                }
                await this.handleExternalSeriesBackwardCompatibility(series);
                this.loadDataForExternalGoogleAnalyticsSeries(series);
            }
        }
    }
}
